import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  Flex,
  Header,
  Segment,
  Dropdown,
  FormUi,
  Divider,
  Button,
  Link,
} from 'nallian-shared-ui/lib/components';
import { APP_SUBSCRIPTION_ACTIONS } from '../../constants';

const FluidSegment = styled(Segment)`
  width: 100%;

  && .appDropdown {
    max-width: 40rem;
  }
`;

const AppSubscriptionForm = ({
  communityAppId,
  availableCommunityApps,
  appsubscriptions,
  isNewAppSubscription,
  urlRoot,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();

  const handleSetActiveSubscription = useCallback(
    (_, { value }) => navigate(`${urlRoot}/app-subscriptions/new?communityAppId=${value}`),
    [urlRoot, navigate]
  );

  // filter available community apps with existing already
  const availableCommunityAppOptions = useMemo(
    () =>
      (availableCommunityApps || [])
        .map(c => ({
          key: c.id,
          value: c.id,
          text: `${c.alias || c.appInstallation?.name} (${c.community?.name})`,
        }))
        .filter(c => !appsubscriptions.find(ca => ca.id === c.value)),
    [availableCommunityApps, appsubscriptions]
  );

  const selectedApp = useMemo(() => availableCommunityApps.find(ca => ca.id === communityAppId), [
    communityAppId,
    availableCommunityApps,
  ]);

  if (!availableCommunityAppOptions.length) return null;

  return (
    <Flex fluid alignItems="flex-start" direction="column">
      <Flex fluid margin="0 0 -0.4rem">
        <Header as="h4">
          {selectedApp
            ? `${selectedApp.alias || selectedApp.appInstallation?.name} (${
                selectedApp.community?.name
              })`
            : t('newAppSubscription')}
        </Header>
      </Flex>
      <FluidSegment secondary>
        {search ? (
          <>
            {isNewAppSubscription && (
              <Flex fluid justifyContent="flex-end" margin="0 0 -2rem 0">
                <Link to={`${urlRoot}/app-subscriptions/${APP_SUBSCRIPTION_ACTIONS.NEW}`}>
                  <Button linkbutton icon="close" content={t('close')} />
                </Link>
              </Flex>
            )}
            <Table fluid basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width="6">{t('feature')}</Table.HeaderCell>
                  <Table.HeaderCell width="6" />
                  <Table.HeaderCell width="4" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>{t('noFeaturesDefinedYet')}</Table.Cell>
                  <Table.Cell />
                  <Table.Cell textAlign="right">
                    <Link
                      to={`${urlRoot}/app-subscriptions/${APP_SUBSCRIPTION_ACTIONS.NEW_FEATURE}${search}`}
                    >
                      <Button linkbutton icon="plus" content={t('addFeature')} />
                    </Link>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </>
        ) : (
          <FormUi>
            <Flex fluid direction="column" alignItems="flex-start" className="field">
              <label htmlFor="_">
                <b>{t('application')}</b>
              </label>
              <Dropdown
                search
                selection
                selectOnBlur={false}
                className="appDropdown"
                placeholder={t('selectAnApplication')}
                onChange={handleSetActiveSubscription}
                options={availableCommunityAppOptions}
              />
            </Flex>
          </FormUi>
        )}
      </FluidSegment>
      <Divider />
    </Flex>
  );
};

AppSubscriptionForm.propTypes = {
  urlRoot: PropTypes.string.isRequired,
  communityAppId: PropTypes.string.isRequired,
  availableCommunityApps: PropTypes.array.isRequired,
  appsubscriptions: PropTypes.array.isRequired,
  isNewAppSubscription: PropTypes.bool,
};

AppSubscriptionForm.defaultProps = { isNewAppSubscription: false };

export default AppSubscriptionForm;
